import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaCheck } from 'react-icons/fa';
import { updateEmployeeLoan, fetchEmployeeLoanById } from '../../../../redux/Slices/EmployeeLoansSlice';
import { getEmployees } from '../../../../redux/Slices/authSlice';

const EditEmployeeLoan = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const dir = isRTL ? 'rtl' : 'ltr';
    const textAlign = isRTL ? 'right' : 'left';

    // State
    const [formData, setFormData] = useState({
        employee_id: '',
        amount: '',
        installments_count: '',
        loan_date: '',
        description: '',
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingData, setLoadingData] = useState(true);

    const { currentEmployeeLoan } = useSelector((state) => state.employeeLoans);

    // Dropdowns State - Employee
    const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
    const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
    const [employeePage, setEmployeePage] = useState(1);
    const { employees, employeesPagination } = useSelector((state) => state.auth);
    const employeeSearchInputRef = useRef(null);
    const employeeSearchTimeoutRef = useRef(null);

    // Outside click listener
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.dropdown-wrapper')) {
                setEmployeeDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch initial data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await dispatch(fetchEmployeeLoanById(id)).unwrap();
                const data = response.data || response;
                if (data) {
                    setFormData({
                        employee_id: data.employee_id,
                        amount: data.amount,
                        installments_count: data.installments_count,
                        loan_date: data.loan_date,
                        description: data.description || '',
                    });
                }
            } catch (error) {
                toast.error(t('sidenav.hr.employeeLoansNamespace.errorAction'));
                navigate('/employee-loans');
            } finally {
                setLoadingData(false);
            }
        };
        fetchData();
    }, [id, dispatch, navigate, t]);

    // Load Employees when dropdown opens
    useEffect(() => {
        if (employeeDropdownOpen) {
            dispatch(getEmployees({ page: employeePage, per_page: 10, search: employeeSearchTerm || undefined }));
        }
    }, [employeeDropdownOpen, employeePage, employeeSearchTerm, dispatch]);

    // Search Debounce
    useEffect(() => {
        if (employeeSearchTimeoutRef.current) clearTimeout(employeeSearchTimeoutRef.current);
        employeeSearchTimeoutRef.current = setTimeout(() => {
            if (employeeDropdownOpen) setEmployeePage(1);
        }, 500);
        return () => clearTimeout(employeeSearchTimeoutRef.current);
    }, [employeeSearchTerm, employeeDropdownOpen]);

    const validate = () => {
        const newErrors = {};
        if (!formData.employee_id) newErrors.employee_id = t('sidenav.hr.employeeLoansNamespace.employeeRequired');
        if (!formData.amount) newErrors.amount = t('sidenav.hr.employeeLoansNamespace.amountRequired');
        if (!formData.installments_count) newErrors.installments_count = t('sidenav.hr.employeeLoansNamespace.installmentsRequired');
        if (!formData.loan_date) newErrors.loan_date = t('sidenav.hr.employeeLoansNamespace.dateRequired');
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSubmitting(true);
        try {
            const result = await dispatch(updateEmployeeLoan({ id, data: formData })).unwrap();
            if (result && result.isOffline) {
                localStorage.setItem('offline_add_performed', 'true');
                toast.success(isRTL ? 'تم حفظ التعديلات محلياً وسيتم المزامنة' : 'Changes saved locally and will sync');
            } else {
                toast.success(t('sidenav.hr.employeeLoansNamespace.updateSuccess'));
            }
            navigate('/employee-loans');
        } catch (error) {
            toast.error(error?.message || t('sidenav.hr.employeeLoansNamespace.errorSaving'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const getSelectedEmployeeName = () => {
        if (!formData.employee_id) return t('sidenav.hr.employeeLoansNamespace.employee');
        const emp = employees?.find(e => String(e.id) === String(formData.employee_id));
        if (emp) return isRTL ? emp.name_ar : emp.name_en;
        if (currentEmployeeLoan?.employee && String(currentEmployeeLoan.employee.id) === String(formData.employee_id)) {
            return isRTL ? currentEmployeeLoan.employee.name_ar : currentEmployeeLoan.employee.name_en;
        }
        return formData.employee_id;
    };

    if (loadingData) return <div style={{ padding: '30px', color: 'white' }}>{t('messages.loading')}</div>;

    return (
        <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir, textAlign, overflow: 'visible' }}>
            <div style={{ marginBottom: '30px' }}>
                <button onClick={() => navigate('/employee-loans')} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                    <FaArrowLeft style={{ transform: isRTL ? 'rotate(180deg)' : 'none' }} /> {t('btns.back')}
                </button>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>{t('sidenav.hr.employeeLoansNamespace.edit')}</h1>
            </div>

            <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', padding: '30px', border: '1px solid #333', position: 'relative', overflow: 'visible' }}>
                <form onSubmit={handleSubmit} style={{ overflow: 'visible' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>

                        {/* Employee Dropdown */}
                        <div className="dropdown-wrapper" style={{ position: 'relative', zIndex: employeeDropdownOpen ? 1000 : 1 }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>{t('sidenav.hr.employeeLoansNamespace.employee')} <span style={{ color: '#f44336' }}>*</span></label>
                            <div onClick={() => { setEmployeeDropdownOpen(!employeeDropdownOpen); }} style={{ padding: '12px', backgroundColor: '#1a1f2e', border: errors.employee_id ? '1px solid #f44336' : '1px solid #333', borderRadius: '8px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{getSelectedEmployeeName()}</span>
                                <span style={{ fontSize: '12px', color: '#666' }}>▼</span>
                            </div>
                            {employeeDropdownOpen && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', zIndex: 1000, marginTop: '5px', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 16px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
                                    <div style={{ padding: '10px', backgroundColor: '#1a1f2e' }}>
                                        <input ref={employeeSearchInputRef} autoFocus type="text" placeholder={t('sidenav.hr.employeeLoansNamespace.searchPlaceholder')} value={employeeSearchTerm} onChange={(e) => setEmployeeSearchTerm(e.target.value)} style={{ width: '100%', padding: '10px', backgroundColor: '#14263b', border: '1px solid #333', borderRadius: '4px', color: 'white', outline: 'none' }} />
                                    </div>

                                    {/* Pagination for Dropdown */}
                                    {employeesPagination && employeesPagination.last_page > 1 && (
                                        <div style={{ padding: '10px', borderTop: '1px solid #333', borderBottom: '1px solid #333', background: '#14263b' }}>
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}>
                                                <button type="button" onClick={(e) => { e.stopPropagation(); setEmployeePage(1); }} disabled={(employeesPagination.current_page || 1) === 1} style={{ padding: '4px 8px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>««</button>
                                                <button type="button" onClick={(e) => { e.stopPropagation(); setEmployeePage(prev => Math.max(1, prev - 1)); }} disabled={(employeesPagination.current_page || 1) === 1} style={{ padding: '4px 8px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>‹</button>
                                                {Array.from({ length: Math.min(3, employeesPagination.last_page) }, (_, i) => {
                                                    const pageNum = Math.max(1, Math.min(employeesPagination.last_page - 2, employeesPagination.current_page - 1)) + i;
                                                    if (pageNum <= 0 || pageNum > employeesPagination.last_page) return null;
                                                    return (
                                                        <button key={pageNum} type="button" onClick={(e) => { e.stopPropagation(); setEmployeePage(pageNum); }} style={{ padding: '4px 8px', backgroundColor: pageNum === employeesPagination.current_page ? '#AC2000' : '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>{pageNum}</button>
                                                    );
                                                })}
                                                <button type="button" onClick={(e) => { e.stopPropagation(); setEmployeePage(prev => Math.min(employeesPagination.last_page, prev + 1)); }} disabled={(employeesPagination.current_page || 1) === employeesPagination.last_page} style={{ padding: '4px 8px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>›</button>
                                                <button type="button" onClick={(e) => { e.stopPropagation(); setEmployeePage(employeesPagination.last_page); }} disabled={(employeesPagination.current_page || 1) === employeesPagination.last_page} style={{ padding: '4px 8px', backgroundColor: '#1a1f2e', color: 'white', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10 }}>»»</button>
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ overflowY: 'auto', maxHeight: '350px', padding: '0 10px', paddingBottom: '20px' }}>
                                        {employees?.map(emp => (
                                            <div key={emp.id} onClick={() => { setFormData({ ...formData, employee_id: emp.id }); setEmployeeDropdownOpen(false); }} style={{ padding: '12px', cursor: 'pointer', borderBottom: '1px solid #333', transition: 'background 0.2s', color: 'white' }} onMouseEnter={(e) => e.target.style.backgroundColor = '#2d3343'} onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                                                {isRTL ? emp.name_ar : emp.name_en}
                                            </div>
                                        ))}
                                        {(!employees || employees.length === 0) && (
                                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>{t('sidenav.hr.employeeLoansNamespace.noData')}</div>
                                        )}
                                        <div style={{ height: '80px' }}></div>
                                    </div>
                                </div>
                            )}
                            {errors.employee_id && <span style={{ color: '#f44336', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.employee_id}</span>}
                        </div>

                        {/* Amount */}
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>{t('sidenav.hr.employeeLoansNamespace.amount')} <span style={{ color: '#f44336' }}>*</span></label>
                            <input type="number"  value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: errors.amount ? '1px solid #f44336' : '1px solid #333', borderRadius: '8px', color: 'white', outline: 'none' }} />
                            {errors.amount && <span style={{ color: '#f44336', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.amount}</span>}
                        </div>

                        {/* Installments Count */}
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>{t('sidenav.hr.employeeLoansNamespace.installmentsCount')} <span style={{ color: '#f44336' }}>*</span></label>
                            <input type="number" value={formData.installments_count} onChange={(e) => setFormData({ ...formData, installments_count: e.target.value })} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: errors.installments_count ? '1px solid #f44336' : '1px solid #333', borderRadius: '8px', color: 'white', outline: 'none' }} />
                            {errors.installments_count && <span style={{ color: '#f44336', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.installments_count}</span>}
                        </div>

                        {/* Date */}
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>{t('sidenav.hr.employeeLoansNamespace.loanDate')} <span style={{ color: '#f44336' }}>*</span></label>
                            <input type="date" value={formData.loan_date} onChange={(e) => setFormData({ ...formData, loan_date: e.target.value })} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: errors.loan_date ? '1px solid #f44336' : '1px solid #333', borderRadius: '8px', color: 'white', outline: 'none' }} />
                            {errors.loan_date && <span style={{ color: '#f44336', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.loan_date}</span>}
                        </div>
                    </div>

                    <div style={{ marginBottom: '30px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>{t('sidenav.hr.employeeLoansNamespace.description')}</label>
                        <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="4" style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', resize: 'vertical', outline: 'none' }} />
                    </div>

                    <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                        <button
                            type="button"
                            onClick={() => navigate('/employee-loans')}
                            disabled={isSubmitting}
                            style={{
                                padding: '12px 30px',
                                backgroundColor: '#666',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                fontWeight: 'bold',
                                fontSize: '16px'
                            }}
                        >
                            {t('btns.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{
                                padding: '12px 30px',
                                backgroundColor: '#AC2000',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                            }}
                        >
                            {isSubmitting ? t('btns.saving') : (
                                <>
                                    <FaCheck />
                                    {t('btns.save')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditEmployeeLoan;
